/**
 * AgentConfigPanel — structured display of runtime agent config
 * from the state_initialized event. Shows model, persona, completion params,
 * system prompt, tools, context requirements, and response format.
 */

import { useState, useCallback } from 'react';
import { formatPersonaName } from '../../utils/persona-display';

interface AgentConfigPanelProps {
  config: Record<string, unknown>;
  toolNames?: string[];
  /**
   * Approximate size, in characters, of the assembled session context /
   * message window (the SDK-owned conversation messages). Rendered as an
   * approximate token estimate alongside the system-prompt size so a large
   * `.shogunchop/` steering bundle is observable to the user.
   * Omitted at call sites without message data.
   */
  sessionContextChars?: number;
}

/**
 * Approximate token count from character length, using the common ~4-chars-per-token
 * heuristic. This is a ROUGH estimate, not a real model-specific tokenizer count —
 * the UI labels every value produced from it as approximate ("~N tokens (approx)").
 *
 * Kept as a module-local (non-exported) helper on purpose: exporting a
 * non-component value from a component module disables @vitejs/plugin-react Fast
 * Refresh for the file (the module then never HMR-updates in the running web app).
 */
function estimateTokens(chars: number): number {
  if (chars <= 0) return 0;
  return Math.ceil(chars / 4);
}

/**
 * Resolve the built system prompt (Messages[0].Content) from the runtime
 * agent_config emitted on `state_initialized`, falling back to the system-role
 * message inside `completion_params.messages` for config shapes that carry it there.
 */
function resolveSystemPrompt(config: Record<string, unknown>): string | undefined {
  const direct = config.system_prompt as string | undefined;
  if (direct) return direct;
  const params = config.completion_params as Record<string, unknown> | undefined;
  const messages = params?.messages as Array<Record<string, unknown>> | undefined;
  const systemMsg = messages?.find(m => m.role === 'system');
  return systemMsg?.content as string | undefined;
}

// Approximate — labeled as such in the UI.
const APPROX_TITLE =
  'Approximate — character-based estimate (~4 chars/token), not an exact tokenizer count';

/**
 * ContextSizeSection — surfaces the (approximate) token size of the system prompt
 * (Messages[0], which carries the injected workspace-steering bundle) and of the
 * assembled session context, so a large steering bundle is visible rather than
 * silent.
 */
function ContextSizeSection({
  systemPromptChars,
  sessionContextChars,
}: {
  systemPromptChars: number;
  sessionContextChars?: number;
}) {
  return (
    <div className="space-y-0.5" data-testid="context-size">
      <div className="flex items-baseline gap-1.5 text-[10px]" title={APPROX_TITLE}>
        <span className="text-muted-foreground shrink-0">System prompt:</span>
        <span className="font-mono text-foreground" data-testid="context-size-system-prompt">
          ~{estimateTokens(systemPromptChars).toLocaleString()} tokens
        </span>
        <span className="text-muted-foreground/70">(approx)</span>
      </div>
      {sessionContextChars != null && (
        <div className="flex items-baseline gap-1.5 text-[10px]" title={APPROX_TITLE}>
          <span className="text-muted-foreground shrink-0">Session context:</span>
          <span className="font-mono text-foreground" data-testid="context-size-session">
            ~{estimateTokens(sessionContextChars).toLocaleString()} tokens
          </span>
          <span className="text-muted-foreground/70">(approx)</span>
        </div>
      )}
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5 text-[10px]">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className="font-mono text-foreground truncate" title={value}>{value}</span>
    </div>
  );
}

function CompletionParams({ config }: { config: Record<string, unknown> }) {
  const params = config.completion_params as Record<string, unknown> | undefined;
  if (!params) return null;

  const entries: Array<{ key: string; value: string }> = [];
  for (const key of ['temperature', 'top_p', 'stop', 'reasoning_effort'] as const) {
    const val = params[key];
    if (val != null) {
      entries.push({ key, value: typeof val === 'object' ? JSON.stringify(val) : String(val) });
    }
  }

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px]">
      {entries.map(({ key, value }) => (
        <span key={key}>
          <span className="text-muted-foreground">{key}:</span>{' '}
          <span className="font-mono text-foreground">{value}</span>
        </span>
      ))}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-accent/50 text-muted-foreground transition-colors"
      title="Copy to clipboard"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function SystemPromptSection({ config }: { config: Record<string, unknown> }) {
  const systemPrompt = resolveSystemPrompt(config);

  if (!systemPrompt) return null;

  const lineCount = systemPrompt.split('\n').length;

  return (
    <details>
      <summary className="px-2 py-1 text-[10px] font-medium cursor-pointer hover:bg-accent/50 flex items-center gap-1.5 text-muted-foreground">
        System Prompt
        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{lineCount} lines</span>
      </summary>
      <div className="px-2 py-1.5">
        <div className="flex justify-end mb-1">
          <CopyButton text={systemPrompt} />
        </div>
        <pre className="text-[10px] font-mono bg-muted/30 rounded p-1.5 whitespace-pre-wrap break-words overflow-x-auto max-h-[400px] overflow-y-auto">
          {systemPrompt}
        </pre>
      </div>
    </details>
  );
}

function ToolsSection({ toolNames }: { toolNames: string[] }) {
  return (
    <details>
      <summary className="px-2 py-1 text-[10px] font-medium cursor-pointer hover:bg-accent/50 flex items-center gap-1.5 text-muted-foreground">
        Tools
        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{toolNames.length}</span>
      </summary>
      <div className="px-2 py-1.5">
        <div className="flex flex-wrap gap-1">
          {toolNames.map(name => (
            <span key={name} className="text-[10px] bg-muted px-1 py-0.5 rounded font-mono">
              {name}
            </span>
          ))}
        </div>
      </div>
    </details>
  );
}

function ResponseFormatSection({ format }: { format: unknown }) {
  return (
    <details>
      <summary className="px-2 py-1 text-[10px] font-medium cursor-pointer hover:bg-accent/50 text-muted-foreground">
        Response Format
      </summary>
      <div className="px-2 py-1.5">
        <pre className="text-[10px] font-mono bg-muted/30 rounded p-1.5 overflow-x-auto">
          {JSON.stringify(format, null, 2)}
        </pre>
      </div>
    </details>
  );
}

export function AgentConfigPanel({ config, toolNames, sessionContextChars }: AgentConfigPanelProps) {
  const providerType = config.provider_type as string | undefined;
  const modelName = config.model_name as string | undefined;
  const personaName = config.persona_name as string | undefined;
  const sessionId = config.session_id as string | undefined;
  const nodeId = config.node_id as string | undefined;
  const responseFormat = config.response_format;
  const systemPromptChars = (resolveSystemPrompt(config) ?? '').length;

  return (
    <div className="space-y-1.5">
      {/* Header fields */}
      <div className="space-y-0.5">
        {(providerType || modelName) && (
          <KeyValue label="Model" value={[providerType, modelName].filter(Boolean).join(' / ')} />
        )}
        {personaName && <KeyValue label="Persona" value={formatPersonaName(personaName)} />}
        {sessionId && <KeyValue label="Session" value={sessionId.slice(0, 8) + '...'} />}
        {nodeId && <KeyValue label="Node" value={nodeId} />}
      </div>

      {/* Context size — approximate system-prompt + session-context token estimates,
          so a large workspace-steering bundle is visible. */}
      {(systemPromptChars > 0 || sessionContextChars != null) && (
        <ContextSizeSection
          systemPromptChars={systemPromptChars}
          sessionContextChars={sessionContextChars}
        />
      )}

      {/* Completion params (inline, not collapsible) */}
      <CompletionParams config={config} />

      {/* System prompt — collapsible, collapsed by default */}
      <SystemPromptSection config={config} />

      {/* Tools — collapsible, collapsed by default */}
      {toolNames && toolNames.length > 0 && <ToolsSection toolNames={toolNames} />}

      {/* Response format — collapsible, collapsed by default */}
      {responseFormat != null && <ResponseFormatSection format={responseFormat} />}
    </div>
  );
}
